import { useMemo, useRef, useState } from 'react';
import { Building2, ChevronDown, Check, Search } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

interface Project {
    id: number;
    name: string;
}

// Dois modos no mesmo componente: a Dashboard filtra por VÁRIOS projetos, e as demais telas
// (MetaCampaigns, MetaOverall, PageviewAnalytics, LeadJourney) continuam com um só. A união
// discriminada mantém cada chamador com o tipo certo — no modo simples, `value` é o id.
// `placeholder` (opt-in): telas que exigem um projeto mostram isto quando nada está escolhido,
// em vez de "Todos os projetos" (que prometeria um filtro que a tela não tem).
type CommonProps = { projects?: Project[]; isOwner: boolean; placeholder?: string };
type SingleProps = CommonProps & { multiple?: false; value: number | undefined; onChange: (val: number | undefined) => void };
type MultiProps = CommonProps & { multiple: true; value: number[]; onChange: (val: number[]) => void };
type CompactProjectSelectProps = SingleProps | MultiProps;

export function CompactProjectSelect(props: CompactProjectSelectProps) {
    const { projects, multiple } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [busca, setBusca] = useState('');
    // Ligado por padrão. `multiple` diz o que a TELA suporta; isto diz o que o usuário quer
    // agora — quem filtra por vários projetos na maior parte do tempo, de vez em quando quer
    // olhar um sozinho sem ter que desmarcar os outros na mão.
    const [modoMultiplo, setModoMultiplo] = useState(true);
    const campoBusca = useRef<HTMLInputElement>(null);

    // Só existe onde a tela aceita vários: nas de projeto único a API recebe um id, e a chave
    // prometeria algo que o chamador não sabe receber.
    const multiploAtivo = !!multiple && modoMultiplo;

    const selecionados: number[] = multiple
        ? (props.value || [])
        : (props.value !== undefined ? [props.value] : []);

    // Despacha pelo PROP, nunca pelo modo: a assinatura do `onChange` de quem chama depende de
    // `multiple`. Com a chave desligada a Dashboard continua recebendo `number[]`, com 0 ou 1.
    const aplicar = (ids: number[]) => {
        if (multiple) props.onChange(ids);
        else props.onChange(ids[0]);
    };

    // Ordem alfabética respeitando acento e caixa — 'Área' tem que cair perto de 'Auto',
    // não no fim da lista como o sort padrão faria.
    const ordenados = useMemo(
        () => [...(projects || [])].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })),
        [projects],
    );
    const termo = busca.trim().toLowerCase();
    const visiveis = termo ? ordenados.filter((p) => p.name.toLowerCase().includes(termo)) : ordenados;

    const marcados = new Set(selecionados);
    const clicar = (id: number) => {
        if (!multiploAtivo) {
            aplicar([id]);
            setIsOpen(false);
            return;
        }
        const proximo = new Set(marcados);
        if (proximo.has(id)) proximo.delete(id);
        else proximo.add(id);
        aplicar([...proximo]);
    };

    // Desligar a chave com vários marcados guarda UM e descarta o resto. O escolhido é o
    // primeiro da lista, não o primeiro que foi clicado: a ordem de clique não está na tela, a
    // da lista está — dá pra ver qual vai sobrar antes de virar a chave.
    const alternarModo = (ligado: boolean) => {
        setModoMultiplo(ligado);
        if (!ligado && selecionados.length > 1) {
            const primeiro = ordenados.find((p) => marcados.has(p.id));
            aplicar(primeiro ? [primeiro.id] : []);
        }
    };

    // Rótulo: 1 projeto mostra o nome; vários mostram a contagem (nomes concatenados não cabem
    // no botão, e truncar no meio esconde justamente o que diferencia um projeto do outro).
    // Tudo selecionado = "Todos os projetos", que é o mesmo relatório de nenhum selecionado.
    const rotulo = selecionados.length === 0 && props.placeholder
        ? props.placeholder
        : props.placeholder && selecionados.length === 1
            ? (ordenados.find((p) => p.id === selecionados[0])?.name || '1 projeto')
        : selecionados.length === 0 || selecionados.length === ordenados.length
        ? 'Todos os projetos'
        : selecionados.length === 1
            ? (ordenados.find((p) => p.id === selecionados[0])?.name || '1 projeto')
            : `${selecionados.length} projetos`;

    const abrir = () => {
        setIsOpen((v) => !v);
        setBusca('');
        // Foca a busca ao abrir: com dezenas de projetos, digitar acha mais rápido que rolar.
        setTimeout(() => campoBusca.current?.focus(), 0);
    };

    return (
        <div className="relative w-full sm:w-auto">
            <button
                onClick={abrir}
                className="w-full sm:w-auto flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded-md hover:border-foreground/40 transition-colors text-sm min-w-0 sm:min-w-[180px]"
            >
                <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium text-foreground flex-1 text-left truncate">{rotulo}</span>
                {multiploAtivo && selecionados.length > 1 && selecionados.length < ordenados.length && (
                    <span className="px-1.5 py-0.5 border border-border text-muted-foreground rounded text-xs font-semibold">
                        {selecionados.length}
                    </span>
                )}
                <ChevronDown
                    className={`h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    {/* Mesma estrutura do seletor de domínios (cabeçalho de ações, dica, lista com
                        ✓ no item escolhido, rodapé com contagem): eles ficam lado a lado no filtro,
                        e dois desenhos diferentes pra mesma função só dão trabalho de leitura. */}
                    <div className="absolute top-full left-0 right-0 sm:right-auto mt-1 z-50 w-full sm:min-w-[280px] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                        <div className="p-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                            <span className="text-xs font-semibold text-gray-600 uppercase">
                                Selecionar {multiploAtivo ? 'projetos' : 'projeto'}
                            </span>
                            <div className="flex items-center gap-1">
                                {/* Preso ao modo múltiplo: com a chave desligada só cabe um projeto,
                                    e um botão "Todos" ali prometeria o que o clique não faz. */}
                                {multiploAtivo && (
                                    <button
                                        onClick={() => aplicar(ordenados.map((p) => p.id))}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                    >
                                        Todos
                                    </button>
                                )}
                                <button
                                    onClick={() => { aplicar([]); if (!multiploAtivo) setIsOpen(false); }}
                                    className="text-xs text-gray-600 hover:text-gray-700 font-medium px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                                >
                                    Limpar
                                </button>
                            </div>
                        </div>

                        {/* Fora da faixa da dica: ali o texto já ocupa três linhas nessa largura, e
                            espremer a chave ao lado levava a quatro. Linha própria custa 33px e
                            deixa o rótulo inteiro legível. */}
                        {multiple && (
                            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-3">
                                <span className="text-xs text-gray-600">Seleção múltipla</span>
                                <Switch
                                    checked={modoMultiplo}
                                    onCheckedChange={alternarModo}
                                    aria-label="Seleção múltipla de projetos"
                                />
                            </div>
                        )}

                        <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
                            <p className="text-xs text-blue-700">
                                💡 <strong>Dica:</strong>{' '}
                                {!multiple
                                    ? 'digite para filtrar a lista pelo nome do projeto'
                                    : multiploAtivo
                                        ? 'clique para somar projetos ao filtro; sem nenhum selecionado, o relatório traz todos'
                                        : 'clique para trocar de projeto; sem nenhum selecionado, o relatório traz todos'}
                            </p>
                        </div>

                        <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                <input
                                    ref={campoBusca}
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                    placeholder="Buscar projeto…"
                                    className="w-full pl-8 pr-2 py-1.5 bg-white text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                                />
                            </div>
                        </div>

                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                            Projetos
                        </div>
                        <div className="p-1 max-h-64 overflow-y-auto">
                            {visiveis.map((project) => {
                                const marcado = marcados.has(project.id);
                                return (
                                    <button
                                        key={project.id}
                                        onClick={() => clicar(project.id)}
                                        className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm flex items-center justify-between gap-2 ${
                                            marcado ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'
                                        }`}
                                    >
                                        <span className="truncate">{project.name}</span>
                                        {marcado && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                                    </button>
                                );
                            })}
                            {visiveis.length === 0 && (
                                <p className="px-3 py-2 text-xs text-gray-500">Nenhum projeto com esse nome.</p>
                            )}
                        </div>

                        {multiploAtivo && selecionados.length > 0 && (
                            <div className="p-2 border-t border-gray-100 bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-600">
                                        {selecionados.length} de {ordenados.length} selecionados
                                    </span>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                                    >
                                        Aplicar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
